import React, { useState, useEffect, memo } from 'react';
import { Card, Space, DatePicker, Button, Spin, Skeleton, message, Segmented, Statistic, Row, Col } from 'antd';
import { MetricCardHeader } from "./Shared/MetricCardHeader";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarOutlined, ThunderboltOutlined, MessageOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { getCostTrend } from '../../api/api';
import { 
  formatXAxisLabel, 
  formatTooltipLabel, 
  formatPeriodDisplay,
  getXAxisInterval,
  type AggregationType 
} from '../../utils/dateFormatters';
import { chartColors, chartConfig } from '../../utils/chartColors';

const { RangePicker } = DatePicker;

interface CostTrendCardProps {
  isExpanded?: boolean;
  dashboardData?: any;
}

export const CostTrendCard: React.FC<CostTrendCardProps> = memo(({ isExpanded = false, dashboardData }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any | null>(null);
  const [aggregation, setAggregation] = useState<AggregationType>('daily');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let from_date: string;
      let to_date: string;
      
      // Calculate date range based on aggregation
      if (aggregation === 'daily') {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
          setLoading(false);
          return;
        }
        from_date = dateRange[0].format('YYYY-MM-DD');
        to_date = dateRange[1].format('YYYY-MM-DD');
      } else if (aggregation === 'weekly') {
        // Last 52 weeks
        const end = dayjs();
        const start = end.subtract(52, 'week');
        from_date = start.format('YYYY-MM-DD');
        to_date = end.format('YYYY-MM-DD');
      } else {
        // Monthly - last 12 months
        const end = dayjs();
        const start = end.subtract(12, 'month');
        from_date = start.format('YYYY-MM-DD');
        to_date = end.format('YYYY-MM-DD');
      }
      
      const response = await getCostTrend(aggregation, from_date, to_date);
      setData(response);
    } catch (error) {
      message.error('Failed to load cost trend');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Skip initial fetch in expanded mode - let user apply filters
    if (isExpanded) return;
    
    // For collapsed view, use dashboard data
    if (dashboardData?.cost_trend) {
      setData(dashboardData.cost_trend);
    }
  }, [isExpanded, dashboardData]);

  // Skeleton loader for collapsed view
  if (loading && !isExpanded) {
    return (
      <div style={{ padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  // Show loading spinner
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" tip="Loading cost trend..." />
      </div>
    );
  }

  // Show empty state with helpful message
  const hasData = data && data.data && data.data.length > 0;

  // Calculate summary statistics from data points
  const dataPoints = data?.data || [];
  const totalCost = dataPoints.reduce((sum: number, point: any) => sum + (point.total_cost || 0), 0);
  const totalTokensIn = dataPoints.reduce((sum: number, point: any) => sum + (point.tokens_in || 0), 0);
  const totalTokensOut = dataPoints.reduce((sum: number, point: any) => sum + (point.tokens_out || 0), 0);
  const totalTokens = totalTokensIn + totalTokensOut;
  const totalMessages = dataPoints.reduce((sum: number, point: any) => sum + (point.message_count || 0), 0);
  const avgCostPerMessage = totalMessages > 0 ? (totalCost / totalMessages) : 0;
  
  let emptyStateMessage;
  if (isExpanded) {
    emptyStateMessage = data === null 
      ? 'Select filters and click "Apply Filters" to load cost data' 
      : 'No cost data available for the selected period. Ensure analytics processor has run.';
  } else {
    emptyStateMessage = 'No cost data available for the last 30 days. Run analytics processor to generate data.';
  }

  const renderCollapsedView = () => {
    if (!hasData) {
      return <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>{emptyStateMessage}</div>;
    }

    const config = data.config || {};

    return (
      <>
        {/* Summary Statistics for Dashboard View */}
        <div className="mb-4">
           {/* Primary Metric: Total Cost with Trend */}
           <MetricCardHeader 
              title="Total Cost"
              value={`$${totalCost.toFixed(2)}`}
              trend={dashboardData?.cost_trend?.trend}
              subtitle={`$${avgCostPerMessage.toFixed(4)} avg/msg • ${totalTokens.toLocaleString()} tokens`}
           />
        </div>

        <div style={{ height: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data.data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
              tickFormatter={(val) => formatXAxisLabel(val, 'daily')}
              interval={getXAxisInterval(data.data?.length || 0)}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              formatter={(value: number) => [`$${value.toFixed(4)}`, undefined]}
              labelFormatter={(label) => formatTooltipLabel(label, 'daily')}
              contentStyle={{ fontSize: 13 }}
            />
            <Area
              type="monotone"
              dataKey="cost_input_uncached"
              stackId="1"
              stroke={chartColors.primary}
              fill={chartColors.primary}
              name="Input (Uncached)"
              activeDot={{ r: 6, stroke: chartColors.primary, strokeWidth: 2 }}
              dot={{ r: 4, fill: chartColors.primary, stroke: '#fff', strokeWidth: 2 }}
            />
            <Area
              type="monotone"
              dataKey="cost_input_cached"
              stackId="1"
              stroke={chartColors.info}
              fill={chartColors.info}
              name="Input (Cached)"
              activeDot={{ r: 6, stroke: chartColors.info, strokeWidth: 2 }}
              dot={{ r: 4, fill: chartColors.info, stroke: '#fff', strokeWidth: 2 }}
            />
            <Area
              type="monotone"
              dataKey="cost_output"
              stackId="1"
              stroke={chartColors.success}
              fill={chartColors.success}
              name="Output"
              activeDot={{ r: 6, stroke: chartColors.success, strokeWidth: 2 }}
              dot={{ r: 4, fill: chartColors.success, stroke: '#fff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
        </div>

        <div style={{ marginTop: 12, fontSize: 12, color: chartColors.textTertiary, textAlign: 'center' }}>
          Per-model pricing · Fallback: ${config.input_price_per_million || 0}/M input, ${config.output_price_per_million || 0}/M output
        </div>
      </>
    );
  };


  const renderExpandedView = () => {
    return (
      <>
        {/* Controls Row */}
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Space wrap>
            <span style={{ fontSize: 13, color: chartColors.textSecondary }}>Aggregation:</span>
            <Segmented
              options={[
                { label: 'Daily', value: 'daily' },
                { label: 'Weekly', value: 'weekly' },
                { label: 'Monthly', value: 'monthly' }
              ]}
              value={aggregation}
              onChange={(value) => setAggregation(value as AggregationType)}
            />
            {aggregation === 'daily' ? (
              <>
                <span style={{ fontSize: 13, color: chartColors.textSecondary, marginLeft: 16 }}>Date Range:</span>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setDateRange([dates[0], dates[1]]);
                    }
                  }}
                  format="YYYY-MM-DD"
                  style={{ width: 260 }}
                />
              </>
            ) : (
              <span style={{ fontSize: 13, color: chartColors.textSecondary, marginLeft: 16, fontStyle: 'italic' }}>
                Showing last {aggregation === 'weekly' ? '52 weeks' : '12 months'}
              </span>
            )}
          </Space>
          
          <Space>
            <Button type="primary" onClick={fetchData} loading={loading}>
              Apply Filters
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchData} disabled={!hasData}>
              Refresh
            </Button>
          </Space>
        </Space>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Spin size="large" tip="Loading cost data..." />
          </div>
        ) : !hasData ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: chartColors.textTertiary }}>
            <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to view cost trend</p>
            <p style={{ fontSize: 14 }}>Configure filters above and click "Apply Filters" to load data</p>
          </div>
        ) : (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic
                  title="Total Cost"
                  value={totalCost}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: chartColors.primary }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Avg Cost / Message"
                  value={avgCostPerMessage}
                  precision={6}
                  prefix="$"
                  valueStyle={{ color: chartColors.success }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Tokens"
                  value={totalTokens}
                  prefix={<ThunderboltOutlined />}
                  valueStyle={{ color: chartColors.warning }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Messages"
                  value={totalMessages}
                  prefix={<MessageOutlined />}
                  valueStyle={{ color: chartColors.purple }}
                />
              </Col>
            </Row>

            <div style={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: number) => `$${value.toFixed(4)}`} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="cost_input_uncached"
                  stackId="1"
                  stroke={chartColors.primary}
                  fill={chartColors.primary}
                  name="Input (Uncached)"
                />
                <Area
                  type="monotone"
                  dataKey="cost_input_cached"
                  stackId="1"
                  stroke={chartColors.info}
                  fill={chartColors.info}
                  name="Input (Cached)"
                />
                <Area
                  type="monotone"
                  dataKey="cost_output"
                  stackId="1"
                  stroke={chartColors.success}
                  fill={chartColors.success}
                  name="Output"
                />
              </AreaChart>
            </ResponsiveContainer>
            </div>

            <div style={{ marginTop: 16, fontSize: 13, color: chartColors.textSecondary }}>
              Per-model pricing · Fallback: ${data.config?.input_price_per_million || 0}/M input, ${data.config?.output_price_per_million || 0}/M output
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div style={{ height: '100%' }}>
      {isExpanded ? renderExpandedView() : renderCollapsedView()}
    </div>
  );
});

CostTrendCard.displayName = 'CostTrendCard';
